<script lang="ts" setup>
import { computed } from 'vue';
import dayjs from 'dayjs';
import LiveTag from '../components/LiveTag.vue';
import type { TimePeriodInfo } from '../types';
import { GLOBAL_TIME_LIVE_FORMAT, GLOBAL_TIME_RANGE_FORMAT } from '../constants';
import { returnMatchingFormat } from '@/common/utils/core/time/parse';

interface Props {
  periodInfo: TimePeriodInfo;
  /** 표시 모드: 'live' = 현재 시간만, 'range' = 범위 */
  format?: 'live' | 'range';
  /** Live 모드 시간 포맷 */
  liveFormat?: string;
  /** Range 모드 시간 포맷 */
  rangeFormat?: string;
  /** Live 태그 표시 여부 */
  showLiveTag?: boolean;
  /** 비활성화 여부 */
  disabled?: boolean;
}

interface Slots {
  default?: (props: { fromTime: string; toTime: string; isLive: boolean }) => unknown;
  liveTag?: () => unknown;
}

const props = withDefaults(defineProps<Props>(), {
  format: 'live',
  liveFormat: GLOBAL_TIME_LIVE_FORMAT,
  rangeFormat: GLOBAL_TIME_RANGE_FORMAT,
  showLiveTag: true,
  disabled: false,
});

defineSlots<Slots>();

// ============================================================
// Computed
// ============================================================

const isLive = computed(() => !props.periodInfo.isPaused && props.periodInfo.timePeriod !== 'pa');

const formattedTime = computed(() => {
  const { fromTime, toTime } = props.periodInfo;

  if (props.format === 'live') {
    const sourceFormat = returnMatchingFormat(toTime) || GLOBAL_TIME_RANGE_FORMAT;
    return dayjs(toTime, sourceFormat).format(props.liveFormat);
  }

  return `${fromTime} ~ ${toTime}`;
});

const displayFromTime = computed(() => {
  const { fromTime } = props.periodInfo;
  const sourceFormat = returnMatchingFormat(fromTime) || GLOBAL_TIME_RANGE_FORMAT;
  return dayjs(fromTime, sourceFormat).format(props.rangeFormat);
});

const displayToTime = computed(() => {
  const { toTime } = props.periodInfo;
  const sourceFormat = returnMatchingFormat(toTime) || GLOBAL_TIME_RANGE_FORMAT;
  return dayjs(toTime, sourceFormat).format(props.rangeFormat);
});
</script>

<template>
  <div
    :class="[
      'time-period-display',
      {
        'time-period-display--live': isLive && props.showLiveTag,
        'time-period-display--paused': !isLive,
      },
    ]"
  >
    <slot
      :from-time="displayFromTime"
      :to-time="displayToTime"
      :is-live="isLive"
    >
      <div class="time-period-display__content">
        <slot name="liveTag">
          <live-tag
            v-if="props.showLiveTag && isLive"
            :disabled="props.disabled"
            class="time-period-display__live-tag"
          />
        </slot>
        <span class="time-period-display__time">
          {{ formattedTime }}
        </span>
      </div>
    </slot>
  </div>
</template>

<style lang="scss">
@property --animate-duration {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

@property --gradient-color {
  syntax: '<color>';
  initial-value: var(--color-green-05);
}

@keyframes gradient-rotate {
  0% {
    --animate-duration: 0deg;
  }
  100% {
    --animate-duration: 360deg;
  }
}

@keyframes bg-pulse {
  0% {
    background-color: var(--color-gray-12-00);
  }
  50% {
    background-color: var(--bg-pulse-highlight, var(--color-green-09-00));
  }
  100% {
    background-color: var(--color-gray-12-00);
  }
}

@mixin animated-conic-border($edgeColor: var(--gradient-color)) {
  position: relative;
  padding: 1px;
  overflow: hidden;
  border-radius: 2px;

  &::before,
  &::after {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 2px;
    background-image: conic-gradient(
      from var(--animate-duration) at 50% 50%,
      var(--input-text-border-color) 0%,
      var(--input-text-border-color) 20%,
      #{$edgeColor} 50%,
      #{$edgeColor} 66%,
      var(--input-text-border-color) 100%
    );
    background-position: 0 0;
    background-size: 100% 100%;
    animation: gradient-rotate 3s infinite linear;
    content: '';
  }

  &::before {
    filter: blur(20px);
  }
}

@mixin pulsing-bg {
  background-color: var(--color-gray-12-00);
  animation: bg-pulse 3s ease-in-out infinite;
}
.time-period-display {
  display: flex;
  position: relative;
  gap: 8px;
  align-items: center;
  width: 200px;
  height: var(--tc-height, 24px);
  border: 1px solid var(--input-text-border-color);
  border-radius: 2px;
  color: var(--main-font-color);

  &--live {
    --gradient-color: var(--color-green-05);

    border: none;

    @include animated-conic-border(var(--gradient-color));

    .time-period-display__content {
      @include pulsing-bg();
    }
  }

  &__content {
    display: flex;
    position: relative;
    z-index: 1;
    gap: 8px;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0 4px;
    background-color: var(--color-gray-12-00);
  }

  &__live-tag.status-tag {
    min-width: 35px;
  }

  &__time {
    margin-left: -4px;
    transition: margin-left $animate-base;
  }

  &--paused {
    .time-period-display__time {
      margin-left: 4px;
    }
  }
}
</style>
